import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, StatCard } from '../../ui';
import { CalendarClock } from 'lucide-react';
import { formatIstDate } from '../../lib/time';
import { request } from '../../api/client';
import { LedgerStatement } from '../../components/LedgerStatement';
import { inr2 } from '../../lib/format';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/**
 * The retailer's own statement of account — credit summary + a running ledger (debits = invoices,
 * credits = payments) with each row deep-linking to its order. Self-scoped via /retailers/me.
 */
export function MyAccountPage() {
  const { data: me, isLoading, isError } = useQuery({ queryKey: ['my-retailer'], queryFn: () => request<any>({ url: '/retailers/me' }) });
  const { data: ledger, isLoading: ledgerLoading } = useQuery({ queryKey: ['my-ledger'], queryFn: () => request<any[]>({ url: '/retailers/me/ledger' }), enabled: !!me });

  const limit = Number(me?.creditLimit) || 0;
  const out = Number(me?.outstandingBalance) || 0;
  const owed = Math.max(0, out);
  const advance = Math.max(0, -out);
  const available = limit - out;
  // One running balance → one figure: positive = they owe (payable), negative = advance in their credit.
  const owes = out > 0.01, inCredit = out < -0.01;
  const balState = owes ? 'payable' : inCredit ? 'in credit' : 'settled';

  const totals = useMemo(() => {
    let billed = 0, paid = 0;
    for (const e of ledger ?? []) { billed += Number(e.debit || 0); paid += Number(e.credit || 0); }
    return { billed, paid, count: ledger?.length ?? 0 };
  }, [ledger]);

  /*
   * How much of the limit is spoken for. A limit and a balance side by side make the reader do the
   * division; the share is the thing that decides whether the next order will go through, so the
   * page states it. Only meaningful against a limit that exists — an unlimited account has no share.
   */
  const usedPct = limit > 0 ? Math.min(999, Math.round((owed / limit) * 1000) / 10) : null;

  /*
   * The last money that actually arrived. A statement of 200 rows buries it, and "when did I last
   * pay, and how much" is the first question a dealer asks of their own account. Ledger rows are
   * newest-first from the API; take the newest with money IN.
   */
  const lastPayment = useMemo(
    () => (ledger ?? []).find((e: any) => Number(e.credit || 0) > 0 && !e.isMemo) ?? null,
    [ledger],
  );
  const daysSince = (d?: string | null) =>
    (d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)) : null);

  if (isError) return <Alert type="info" title="No account statement is available for this login." />;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="type-page-title text-content">My Account</h1>
        {me && <div className="text-sm text-muted">{me.companyName} · {me.code}</div>}
      </div>

      {/* Status banner */}
      {me && (
        advance > 0 ? (
          <div className="rounded-card border border-success-500/20 bg-success-500/10 px-4 py-3 text-sm text-success-600">
            <span className="font-semibold">You're {inr2(advance)} in credit (advance).</span> {inr(available)} available to place new orders.
          </div>
        ) : owed > 0 ? (
          <div className="rounded-card border border-warning-500/20 bg-warning-500/10 px-4 py-3 text-sm text-warning-600">
            <span className="font-semibold">You owe {inr2(owed)}.</span> {inr(available)} of your {inr(limit)} credit limit is still available.
          </div>
        ) : (
          <div className="rounded-card border border-line bg-surface-2 px-4 py-3 text-sm text-muted">All settled — nothing outstanding.</div>
        )
      )}

      {/*
        * The facts that decide how to read the statement below — the terms an invoice falls due on,
        * who the account is in the name of, and when money last arrived. They were nowhere on this
        * page, so a dealer reading "₹12,480 payable" had no way to know whether it was late.
        */}
      {me && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-card border border-line bg-surface px-4 py-3 shadow-e1">
          <span className="mr-1 shrink-0 type-overline text-subtle">Your terms</span>
          {me.creditPeriodDays != null && (
            <Chip icon={<CalendarClock className="size-3.5" />}>{me.creditPeriodDays}-day credit</Chip>
          )}
          {me.contactPerson && <Chip label="Contact">{me.contactPerson}</Chip>}
          {me.gstNo && <Chip label="GSTIN">{me.gstNo}</Chip>}
          {lastPayment ? (
            <Chip label="Last payment">
              <span className="text-success-600">{inr2(lastPayment.credit)}</span>
              <span className="text-muted"> · {formatIstDate(lastPayment.createdAt)}</span>
              {daysSince(lastPayment.createdAt) != null && (
                <span className="text-subtle"> ({daysSince(lastPayment.createdAt)}d ago)</span>
              )}
            </Chip>
          ) : (
            <Chip label="Last payment"><span className="text-subtle">none recorded yet</span></Chip>
          )}
        </div>
      )}

      {/* KPIs — the balance tile carries one figure whose colour + suffix say whether it is owed or
          in credit; the rest split the limit into what is used, what is left, and what has moved. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label={inCredit ? 'In credit' : 'Outstanding'}
          value={<>{inr(Math.abs(out))}<span className="ml-1 type-label text-muted">{balState}</span></>}
          hint={owes ? 'what you owe right now' : inCredit ? 'advance held on your account' : 'nothing outstanding'}
          tone={owes ? 'danger' : inCredit ? 'success' : 'neutral'}
        />
        <StatCard
          label="Available to order"
          value={inr(available)}
          hint={available > 0 ? 'you can order up to this' : 'limit reached — clear dues to order'}
          tone={available > 0 ? 'brand' : 'danger'}
        />
        <StatCard label="Credit limit" value={inr(limit)} hint="agreed with Green Bay" tone="sky" />
        {/* Bands, not a gradient: under half is headroom, over four-fifths is nearly out. */}
        <StatCard
          label="Credit used"
          value={usedPct == null ? '—' : `${usedPct}%`}
          hint={usedPct == null ? 'no limit set' : usedPct >= 80 ? 'almost at your limit' : usedPct >= 50 ? 'over half your limit' : 'well inside your limit'}
          tone={usedPct == null ? 'neutral' : usedPct >= 80 ? 'danger' : usedPct >= 50 ? 'gold' : 'brand'}
        />
        <StatCard
          label="Billed to date"
          value={inr(totals.billed)}
          hint={`${totals.count} entr${totals.count === 1 ? 'y' : 'ies'} on this statement`}
          tone="neutral"
        />
        <StatCard label="Paid to date" value={inr(totals.paid)} hint="payments and credit notes" tone="success" />
      </div>

      {/* Statement */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="type-section-title text-content">Statement of account</h2>
          <div className="text-xs text-muted">
            Billed <span className="font-semibold text-danger-600">{inr2(totals.billed)}</span> · Paid <span className="font-semibold text-success-600">{inr2(totals.paid)}</span> · {totals.count} entr{totals.count === 1 ? 'y' : 'ies'}
          </div>
        </div>
        {/* The account holder's OWN statement → mirror the frame to their side (payable / in credit). */}
        <LedgerStatement data={ledger ?? []} loading={isLoading || ledgerLoading} perspective="retailer" />
      </div>
    </div>
  );
}

/** One labelled fact in the terms strip — a bordered chip so a value never floats as loose text. */
function Chip({ label, icon, children }: { label?: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-pill border border-line bg-surface-2 px-2.5 py-1 type-label">
      {icon}
      {label && <span className="text-subtle">{label}</span>}
      <span className="truncate type-label-strong text-content">{children}</span>
    </span>
  );
}
